import { useQuery } from '@tanstack/react-query';

import { fbmCatalogApi } from 'api/FBM/fbmCatalog';
import { ApiError } from 'shared/errors/ApiError';

export const FBM_CATALOG_QUERY_KEY = 'fbmCatalog';

export const fbmCatalogQueryKeys = {
    all: [FBM_CATALOG_QUERY_KEY],
    list: (fbmAccountName, params) => [FBM_CATALOG_QUERY_KEY, fbmAccountName, params],
};

/**
 * Fetches the FBM partner catalog for Blue Apron / Marley Spoon.
 *
 * @param {Object} options
 * @param {string} [options.fbmAccountName]
 * @param {'includes_partner' | 'includes_mm' | 'includes_mm_excludes_partner'} options.filter
 * @param {number} [options.page]
 * @param {number} [options.pageSize]
 * @param {number[]} [options.externalSkus]
 * @param {string[]} [options.name]
 * @param {string[]} [options.brand]
 * @param {string[]} [options.ingredients]
 * @param {string[]} [options.allergens]
 * @param {string[]} [options.l1Taxonomies]
 * @param {string[]} [options.l2Taxonomies]
 * @param {string[]} [options.l3Taxonomies]
 * @param {string} [options.sortField]
 * @param {'asc'|'desc'} [options.sortDir]
 * @param {boolean} [options.enabled=true]
 */
export function useFbmCatalogQuery({
    allergens,
    brand,
    enabled = true,
    externalSkus,
    fbmAccountName,
    filter,
    ingredients,
    l1Taxonomies,
    l2Taxonomies,
    l3Taxonomies,
    name,
    page,
    pageSize,
    sortDir,
    sortField,
} = {}) {
    const listParams = {
        allergens,
        brand,
        externalSkus,
        filter,
        ingredients,
        l1Taxonomies,
        l2Taxonomies,
        l3Taxonomies,
        name,
        page,
        pageSize,
        sortDir,
        sortField,
    };

    return useQuery({
        enabled: enabled && !!fbmAccountName && !!filter,
        queryFn: async () => {
            const response = await fbmCatalogApi.getCatalog({
                ...listParams,
                fbmAccount: fbmAccountName,
            });
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch FBM catalog',
                    response?.statusCode ?? 400,
                    response?.data,
                );
            }

            return {
                rows: response.data?.rows ?? [],
                page: response.data?.page ?? page ?? 1,
                pageSize: response.data?.pageSize ?? pageSize ?? 100,
                total: response.data?.total ?? 0,
                totalPages: response.data?.totalPages ?? 0,
            };
        },
        queryKey: fbmCatalogQueryKeys.list(fbmAccountName, listParams),
        retry: (failureCount, err) => {
            if (err instanceof ApiError && (err.isServerError || err.isAuthError || err.isClientError)) {
                return false;
            }
            return failureCount < 2;
        },
        staleTime: 60_000,
        // Keep prior rows for filter/sort/pagination within the same account, but never
        // show another partner's catalog while the new account query is in flight.
        placeholderData: (previousData, previousQuery) => {
            const previousAccountName = previousQuery?.queryKey?.[1];
            if (previousAccountName !== fbmAccountName) {
                return undefined;
            }
            return previousData;
        },
    });
}
